import { useParams, Link } from "wouter";
import { useGetListing, useGetSimilarListings, getGetListingQueryKey, getGetSimilarListingsQueryKey } from "@workspace/api-client-react";
import { useI18n } from "@/contexts/I18nContext";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { MapPin, Store, CheckCircle2, AlertCircle, MessageCircle, Phone, ArrowLeft, ShieldAlert, ArrowRight } from "lucide-react";
import { ListingImage } from "@/components/listing-image";
import { buildListingWhatsAppMessage, buildWhatsAppUrl } from "@/lib/whatsapp";
import {
  trackListingViewed,
  trackInquiryStarted,
  trackWhatsAppClicked,
  trackPhoneClicked,
} from "@/lib/analytics";
import React, { useState, useEffect } from "react";

type LeadState = "idle" | "revealed";

function serviceMethodLabel(method: string) {
  return ({
    merchant_location: "Service at Merchant Location",
    customer_location: "Service at Customer Location / Home Service",
    physical_goods: "Physical Goods",
    delivery: "Delivery Available",
    pickup: "Pickup Available",
    online_remote: "Online / Remote Service",
  } as Record<string, string>)[method] ?? method;
}

export default function ListingDetailPage() {
  const params = useParams();
  const slugOrId = params.slug as string;
  const { t, isRTL } = useI18n();

  const [activeImage, setActiveImage] = useState(0);
  const [leadState, setLeadState] = useState<LeadState>("idle");

  const { data: listing, isLoading, error } = useGetListing(slugOrId, {
    query: { enabled: !!slugOrId, queryKey: getGetListingQueryKey(slugOrId) },
  });

  const { data: similarListings } = useGetSimilarListings(slugOrId, {
    query: { enabled: !!slugOrId, queryKey: getGetSimilarListingsQueryKey(slugOrId) },
  });

  // Track listing view once when the listing loads
  useEffect(() => {
    if (!listing) return;
    trackListingViewed({
      merchant_id: listing.merchantId,
      category: listing.categoryName,
      city: listing.area,
      listing_plan: listing.isFeatured ? "featured" : "basic",
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [listing?.id]);

  // Redirect numeric-ID URLs to canonical slug URLs (no page reload)
  useEffect(() => {
    if (!listing) return;
    const isNumericId = /^\d+$/.test(slugOrId);
    if (isNumericId && listing.slug && listing.slug !== slugOrId) {
      window.history.replaceState(null, "", `/listings/${listing.slug}`);
    }
  }, [listing, slugOrId]);

  // Inject canonical <link> for SEO
  useEffect(() => {
    if (!listing) return;
    const canonicalSlug = listing.slug ?? slugOrId;
    const href = `${window.location.origin}/listings/${canonicalSlug}`;
    let link = document.querySelector<HTMLLinkElement>('link[rel="canonical"]');
    if (!link) {
      link = document.createElement("link");
      link.rel = "canonical";
      document.head.appendChild(link);
    }
    link.href = href;
    return () => { link?.remove(); };
  }, [listing, slugOrId]);

  // Inject Open Graph and Twitter meta tags for rich social previews
  useEffect(() => {
    if (!listing) return;

    const canonicalSlug = listing.slug ?? slugOrId;
    const pageUrl = `${window.location.origin}/listings/${canonicalSlug}`;
    const image =
      (listing.images?.length ? listing.images[0] : undefined) ??
      listing.imageUrl ??
      "";

    const priceStr = listing.priceAed != null ? ` — ${listing.priceAed.toLocaleString()} AED` : "";
    const ogTitle = `${listing.title}${priceStr} | Souq Crypto`;
    const ogDescription =
      listing.description?.slice(0, 160) ||
      `View this listing on Souq Crypto — discover where to spend USDT in Dubai.`;

    // Helper to upsert a <meta> tag by explicit attribute key and value
    const upsertMeta = (
      attrKey: "name" | "property",
      attrValue: string,
      content: string,
    ): HTMLMetaElement => {
      let el = document.querySelector<HTMLMetaElement>(
        `meta[${attrKey}="${attrValue}"]`,
      );
      if (!el) {
        el = document.createElement("meta");
        el.setAttribute(attrKey, attrValue);
        document.head.appendChild(el);
      }
      el.setAttribute("content", content);
      return el;
    };

    // Page title
    const prevTitle = document.title;
    document.title = ogTitle;

    // Open Graph
    upsertMeta("property", "og:title",       ogTitle);
    upsertMeta("property", "og:description", ogDescription);
    upsertMeta("property", "og:url",         pageUrl);
    upsertMeta("property", "og:type",        "product");
    if (image) upsertMeta("property", "og:image", image);

    // Twitter / X
    upsertMeta("name", "twitter:card",        "summary_large_image");
    upsertMeta("name", "twitter:title",       ogTitle);
    upsertMeta("name", "twitter:description", ogDescription);
    if (image) upsertMeta("name", "twitter:image", image);

    return () => {
      // Restore all tags to site-wide defaults (never remove shared tags)
      document.title = prevTitle;
      upsertMeta("property", "og:title",       "Souq Crypto — Discover Where to Spend USDT in Dubai");
      upsertMeta("property", "og:description", "Find Dubai merchants, products, and services that accept USDT directly.");
      upsertMeta("property", "og:url",         window.location.origin);
      upsertMeta("property", "og:type",        "website");
      upsertMeta("property", "og:image",       "/og-default.png");
      upsertMeta("name", "twitter:card",        "summary_large_image");
      upsertMeta("name", "twitter:title",       "Souq Crypto — Discover Where to Spend USDT in Dubai");
      upsertMeta("name", "twitter:description", "Find Dubai merchants, products, and services that accept USDT directly.");
      upsertMeta("name", "twitter:image",       "/og-default.png");
    };
  }, [listing, slugOrId]);

  if (isLoading) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
      </div>
    );
  }

  if (error || !listing) {
    return (
      <div className="flex-1 flex flex-col items-center justify-center p-8 text-center bg-muted/10">
        <AlertCircle className="h-16 w-16 text-destructive mb-6" />
        <h2 className="text-3xl font-extrabold mb-3 text-[#062A60] dark:text-white">{t("Listing not found")}</h2>
        <p className="text-lg text-muted-foreground mb-8 max-w-md">
          {t("The listing you are looking for does not exist or has been removed.")}
        </p>
        <Button size="lg" className="rounded-full px-8 font-bold shadow-md" asChild data-testid="btn-back-to-explore">
          <Link href="/explore">{t("Back to Explore")}</Link>
        </Button>
      </div>
    );
  }

  const images = listing.images?.length
    ? listing.images
    : ([listing.imageUrl].filter(Boolean) as string[]);

  return (
    <div className="flex-1 bg-muted/10 pb-20">
      {listing.isDemo && (
        <div
          className="bg-[#062A60] text-white text-center py-2.5 text-xs font-bold uppercase tracking-widest shadow-md"
          data-testid="banner-demo-listing"
        >
          {t("DEMO LISTING — This is a sample listing for demonstration purposes.")}
        </div>
      )}

      <div className="container mx-auto px-4 pt-8 pb-16 max-w-6xl">
        <Link
          href="/explore"
          className="inline-flex items-center text-sm font-bold text-muted-foreground hover:text-primary mb-8 transition-colors"
          data-testid="link-back-to-listings"
        >
          <ArrowLeft className={`mr-2 h-4 w-4 ${isRTL ? "rtl-flip" : ""}`} /> {t("Back to listings")}
        </Link>

        <div className="grid lg:grid-cols-3 gap-10">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-10">
            {/* Image Gallery */}
            <div className="space-y-4">
              <div className="aspect-[4/3] md:aspect-[16/9] w-full rounded-3xl overflow-hidden border border-border/60 bg-card shadow-sm">
                {images.length > 0 ? (
                  <ListingImage
                    src={images[activeImage]}
                    alt={listing.title}
                    imgClassName="w-full h-full object-cover transition-transform duration-700 hover:scale-105"
                    data-testid="img-listing-main"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-muted-foreground bg-muted">
                    {t("No image available")}
                  </div>
                )}
              </div>
              {images.length > 1 && (
                <div className="flex gap-4 overflow-x-auto pb-4 scrollbar-none">
                  {images.map((img, i) => (
                    <button
                      key={i}
                      onClick={() => setActiveImage(i)}
                      className={`relative w-24 aspect-square rounded-2xl overflow-hidden shrink-0 transition-all duration-200 ${
                        activeImage === i
                          ? "ring-4 ring-primary border-2 border-background shadow-md scale-95"
                          : "border-2 border-transparent opacity-70 hover:opacity-100 hover:scale-105"
                      }`}
                      data-testid={`btn-thumbnail-${i}`}
                    >
                      <ListingImage src={img} alt={`Thumbnail ${i}`} imgClassName="w-full h-full object-cover" />
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Title & Metadata */}
            <div>
              <div className="flex flex-wrap gap-2 mb-6">
                <Badge className="bg-primary hover:bg-primary text-primary-foreground font-bold px-4 py-1.5 text-sm rounded-full shadow-sm">
                  {t("USDT Accepted")}
                </Badge>
                {listing.isFeatured && (
                  <Badge
                    variant="outline"
                    className="border-accent text-accent font-bold px-4 py-1.5 text-sm rounded-full bg-accent/5 shadow-sm"
                  >
                    {t("Featured")}
                  </Badge>
                )}
                <Badge variant="secondary" className="souq-category-badge px-4 py-1.5 text-sm rounded-full font-bold shadow-sm">
                  {listing.categoryName}
                </Badge>
              </div>

              <h1
                className="text-4xl md:text-5xl font-extrabold tracking-tight mb-6 text-[#062A60] dark:text-white leading-tight"
                data-testid="text-listing-title"
              >
                {listing.title}
              </h1>

              <div className="flex flex-wrap items-center gap-8 text-muted-foreground font-semibold mb-8 pb-8 border-b border-border/60">
                <div className="flex items-center gap-3">
                  <div className="h-10 w-10 rounded-full bg-muted flex items-center justify-center">
                    <Store className="h-5 w-5 text-foreground/50" />
                  </div>
                  <span className="text-lg text-foreground" data-testid="text-merchant-name">{listing.merchantName}</span>
                </div>
                <div className="flex items-center gap-3">
                  <div className="h-10 w-10 rounded-full bg-muted flex items-center justify-center">
                    <MapPin className="h-5 w-5 text-foreground/50" />
                  </div>
                  <span className="text-lg text-foreground">{listing.area}</span>
                </div>
              </div>

              <div className="prose prose-lg max-w-none text-muted-foreground">
                <h3 className="text-2xl font-extrabold mb-6 text-[#062A60] dark:text-white">{t("Description")}</h3>
                <p className="whitespace-pre-wrap leading-relaxed text-foreground/80">{listing.description}</p>
              </div>
            </div>

            {/* Merchant Details */}
            <Card className="border-border/60 bg-card rounded-3xl overflow-hidden shadow-sm hover:shadow-md transition-shadow">
              <CardContent className="p-0">
                <Link href={`/merchants/${listing.merchant?.slug ?? listing.merchantId}`} className="group block">
                  <div className="bg-gradient-to-r from-muted to-transparent p-8 flex flex-col sm:flex-row items-center sm:items-start gap-6 text-center sm:text-start transition-colors group-hover:bg-muted/80">
                    <div className="h-20 w-20 rounded-2xl bg-background border border-border/50 flex items-center justify-center text-3xl font-extrabold text-primary shadow-md shrink-0 overflow-hidden ring-4 ring-card">
                      {listing.merchant?.logoUrl ? (
                        <img
                          src={listing.merchant.logoUrl}
                          alt={listing.merchantName}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        listing.merchantName.charAt(0)
                      )}
                    </div>
                    <div className="flex-1">
                      <h3 className="text-2xl font-bold text-[#062A60] dark:text-white group-hover:text-primary transition-colors mb-2">{listing.merchantName}</h3>
                      <p className="text-muted-foreground line-clamp-2 leading-relaxed">{listing.merchant?.description}</p>
                    </div>
                    <div className="hidden sm:flex items-center self-center h-10 w-10 rounded-full bg-background border border-border justify-center group-hover:bg-primary group-hover:text-primary-foreground group-hover:border-primary transition-colors">
                      <ArrowRight className={`h-5 w-5 ${isRTL ? "rtl-flip" : ""}`} />
                    </div>
                  </div>
                </Link>
                {listing.merchant?.serviceMethods?.length > 0 && (
                  <div className="px-8 py-5 border-t border-border/50 flex flex-wrap gap-2 justify-center sm:justify-start">
                    {listing.merchant.serviceMethods.map((method) => (
                      <Badge key={method} variant="secondary" className="bg-muted px-4 py-1.5 font-medium">
                        {t(serviceMethodLabel(method) as Parameters<typeof t>[0])}
                      </Badge>
                    ))}
                  </div>
                )}
                {listing.merchant?.openingHours && (
                  <div className="p-8 border-t border-border/50 flex flex-col sm:flex-row items-center sm:items-start gap-3 text-center sm:text-start">
                    <div className="font-bold text-muted-foreground uppercase tracking-widest text-sm w-full sm:w-32 sm:pt-1">{t("Hours:")}</div>
                    <div className="text-foreground font-medium whitespace-pre-wrap">{listing.merchant.openingHours}</div>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            <Card className="border-primary/20 shadow-2xl shadow-primary/5 rounded-3xl sticky top-24 overflow-hidden">
              <div className="h-2 w-full bg-gradient-to-r from-[#062A60] via-primary to-[#062A60]"></div>
              <CardContent className="p-8">
                {/* Price */}
                <div className="mb-8 text-center md:text-start">
                  <div className="text-sm font-bold tracking-widest uppercase text-muted-foreground mb-2">{t("Price")}</div>
                  <div
                    className="text-4xl md:text-5xl font-extrabold text-[#062A60] dark:text-white mb-2"
                    data-testid="text-listing-price"
                  >
                    {listing.priceAed
                      ? `${listing.priceAed.toLocaleString()} ${t("AED")}`
                      : t("Contact for price")}
                  </div>
                  {listing.priceAed && (
                    <div className="text-base font-semibold text-muted-foreground/70">
                      {t("Approx.")} $
                      {(listing.priceAed / 3.67).toLocaleString(undefined, { maximumFractionDigits: 0 })} USDT
                    </div>
                  )}
                </div>

                {/* USDT Accepted info */}
                <div className="bg-primary/5 border border-primary/20 rounded-2xl p-5 mb-8">
                  <div className="flex items-start gap-4">
                    <CheckCircle2 className="h-7 w-7 text-primary shrink-0 mt-1" />
                    <div>
                      <h4 className="font-bold text-foreground text-lg mb-2">
                        {t("USDT Accepted Directly")}
                      </h4>
                      <p className="text-sm text-muted-foreground leading-relaxed">
                        {t("Arrange payment directly with the merchant.")}{" "}
                        <br/><span className="mt-1 block">{t("Supported networks:")}</span>
                        {listing.supportedNetworks?.length ? (
                          <span className="font-bold text-foreground inline-block mt-1">{listing.supportedNetworks.join(", ")}</span>
                        ) : (
                          <span className="font-bold text-foreground inline-block mt-1">{t("ERC20, TRC20")}</span>
                        )}
                      </p>
                    </div>
                  </div>
                </div>

                {/* ── IDLE: Get Contact Details CTA ───────────────────── */}
                {leadState === "idle" && (
                  <div className="space-y-4 mb-2">
                    <Button
                      className="w-full h-14 text-lg font-bold gap-3 rounded-2xl shadow-lg shadow-primary/20 hover:shadow-primary/40 transition-all hover:-translate-y-0.5"
                      onClick={() => {
                        setLeadState("revealed");
                        trackInquiryStarted({ merchant_id: listing.merchantId, category: listing.categoryName });
                      }}
                      data-testid="btn-request-deal"
                    >
                      <MessageCircle className="h-6 w-6" />
                      {t("Get Contact Details")}
                    </Button>
                    <p className="text-xs text-center text-muted-foreground font-medium leading-relaxed px-4">
                      {t("Contact directly via WhatsApp, phone, or email to confirm details and availability.")}
                    </p>
                  </div>
                )}

                {/* ── REVEALED: Direct Call & WhatsApp buttons ─────────── */}
                {leadState === "revealed" && (
                  <div className="space-y-3 mb-2 animate-in fade-in zoom-in duration-300" data-testid="lead-confirmation">
                    <h4 className="font-bold text-sm uppercase tracking-widest text-muted-foreground mb-4 text-center">
                      {t("Contact Merchant Now")}
                    </h4>

                    {listing.merchant?.whatsapp ? (
                      <Button
                        className="w-full bg-[#25D366] hover:bg-[#128C7E] text-white h-14 text-lg font-bold shadow-lg rounded-xl transition-all hover:-translate-y-0.5"
                        asChild
                        data-testid="btn-contact-whatsapp"
                      >
                        <a
                          href={buildWhatsAppUrl(
                            listing.merchant.whatsapp,
                            buildListingWhatsAppMessage({
                              title: listing.title,
                              merchantName: listing.merchantName,
                              priceAed: listing.priceAed,
                              listingUrl: window.location.href,
                            }),
                          )}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={() => trackWhatsAppClicked({ merchant_id: listing.merchantId, category: listing.categoryName })}
                        >
                          <MessageCircle className="mr-3 h-6 w-6" /> {t("WhatsApp")}
                        </a>
                      </Button>
                    ) : null}

                    {listing.merchant?.phone ? (
                      <Button
                        variant="outline"
                        className="w-full h-14 text-lg font-bold rounded-xl border-border/60 hover:bg-muted transition-all"
                        asChild
                        data-testid="btn-contact-call"
                      >
                        <a
                          href={`tel:${listing.merchant.phone}`}
                          onClick={() => trackPhoneClicked({ merchant_id: listing.merchantId, category: listing.categoryName })}
                        >
                          <Phone className="mr-3 h-5 w-5 text-muted-foreground" /> {t("Call")}
                        </a>
                      </Button>
                    ) : null}

                    {!listing.merchant?.whatsapp && !listing.merchant?.phone && (
                      <p className="text-sm text-center text-muted-foreground py-4">
                        {t("Contact for price")}
                      </p>
                    )}

                    <p className="text-xs text-center text-muted-foreground font-medium leading-relaxed px-4 pt-4 border-t border-border/50">
                      {t("Souq Crypto introduces customers to independent merchants but does not receive, hold, transfer, or process customer funds.")}
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Disclaimer */}
            <div
              className="flex items-start gap-4 text-xs font-medium text-muted-foreground bg-muted/50 p-6 rounded-3xl border border-border/50"
              data-testid="text-disclaimer"
            >
              <ShieldAlert className="h-6 w-6 shrink-0 text-accent mt-0.5" />
              <p className="leading-relaxed">
                {t(
                  "Souq Crypto is an independent business directory. We do not exchange, receive, hold, transfer, or process cryptocurrency or customer funds. Customers and merchants arrange transactions independently and are responsible for confirming all payment terms.",
                )}
              </p>
            </div>
          </div>
        </div>

        {/* Similar Listings */}
        {similarListings && similarListings.length > 0 && (
          <div className="mt-24 pt-16 border-t border-border/60">
            <h2 className="text-3xl font-extrabold mb-10 text-[#062A60] dark:text-white">{t("Similar Listings")}</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
              {similarListings.map((item) => (
                <Link
                  key={item.id}
                  href={`/listings/${item.slug ?? item.id}`}
                  className="block h-full group"
                  data-testid={`card-similar-${item.id}`}
                >
                  <Card className="h-full overflow-hidden rounded-3xl border-border/60 hover:border-primary/50 hover:shadow-xl transition-all duration-300 flex flex-col hover:-translate-y-1">
                    <div className="relative aspect-[4/3] overflow-hidden bg-muted w-full">
                      <ListingImage
                        src={item.imageUrl}
                        alt={item.title}
                        imgClassName="object-cover w-full h-full group-hover:scale-105 transition-transform duration-500"
                      />
                      <div className="absolute top-3 left-3 flex flex-col gap-1.5 items-start">
                        <Badge className="souq-usdt-badge bg-primary/95 hover:bg-primary text-primary-foreground backdrop-blur-sm shadow-sm font-bold px-3 py-1 text-xs">
                          {t("USDT Accepted")}
                        </Badge>
                        {item.isFeatured && (
                          <Badge
                            variant="outline"
                            className="bg-accent/90 border-accent-foreground/10 text-accent-foreground backdrop-blur-sm shadow-sm font-bold px-3 py-1 text-xs"
                          >
                            {t("Premium Only")}
                          </Badge>
                        )}
                      </div>
                       <div className="absolute bottom-3 right-3">
                         <Badge variant="secondary" className="souq-category-badge backdrop-blur-md font-bold shadow-md px-3 py-1.5 text-xs">
                          {item.categoryName}
                        </Badge>
                      </div>
                    </div>
                    
                    <CardContent className="flex-1 flex flex-col p-6">
                      <div className="mb-2">
                        <h3 className="font-bold text-xl line-clamp-2 group-hover:text-primary transition-colors leading-tight">
                          {item.title}
                        </h3>
                      </div>
                      
                      <div className="flex items-center gap-2 text-xs text-muted-foreground mb-4 font-semibold">
                        <Store className="h-3.5 w-3.5 text-foreground/50" />
                        <span className="truncate">{item.merchantName}</span>
                        <span className="text-border mx-1">•</span>
                        <MapPin className="h-3.5 w-3.5 text-foreground/50" />
                        <span className="truncate">{item.area}</span>
                      </div>
                      
                      <div className="pt-5 mt-auto border-t border-border/50 flex items-center justify-between">
                        <div className="font-extrabold text-lg text-[#062A60] dark:text-white">
                          {item.priceAed
                            ? `AED ${item.priceAed.toLocaleString()}`
                            : t("Contact for price")}
                        </div>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="font-bold rounded-full group-hover:bg-primary group-hover:text-primary-foreground transition-all"
                        >
                          {t("View Details")}
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                </Link>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
